/**
 * Pinterest Ads — Row Transformers (Story 19.5, Task 4).
 *
 * Pure functions that convert raw Pinterest analytics rows
 * (`Record<string, unknown>` with SCREAMING_SNAKE_CASE Pinterest column
 * names) into typed camelCase records aligned with `PINTEREST_ADS_METRICS[].key`.
 *
 * Conventions:
 *   - `parseMetric` — null/undefined/non-finite → 0 (consistent with Google
 *     Ads & MS Ads adapter convention).
 *   - `parseMicroDollar` — `parseMetric / 1_000_000`. **Preserves sign** —
 *     Pinterest can return negative `SPEND_REVERSALS_IN_MICRO_DOLLAR` for
 *     refunds (Pre-mortem F5).
 *   - `parsePercent` — defensive normaliser. Pinterest's API returns rates
 *     as fractions in `[0, 1]` (e.g. CTR `0.0234`), but historic versions
 *     have been observed in display-percent form. The `value > 1 ? /100 : value`
 *     guard catches the documented inconsistency without asking widgets to
 *     compensate (Reviewer A).
 *   - **Paid/earned/total triplet preservation** — if the raw row has
 *     `IMPRESSION_1`, `IMPRESSION_2`, AND `TOTAL_IMPRESSION`, the transformer
 *     emits `paidImpressions`, `earnedImpressions`, AND `totalImpressions`.
 *     Never roll up.
 *   - **`EENGAGEMENT_RATE` (double-E)** is the *earned* engagement rate, not
 *     a typo. Maps to `earnedEngagementRate` per integration-mapping
 *     directive line 56.
 *   - Every transformer stamps `_reportType` on its output (Pre-mortem F4
 *     contract).
 *
 * @see _bmad/bmm/data/integration-metrics/pinterest-ads.md
 */

import type { PinterestAdsReportType } from "./analytics";

// ============================================================================
// Primitive parsers
// ============================================================================

/**
 * Coerce a raw Pinterest cell into a finite number. Returns `0` for any
 * null/undefined/non-finite input. **Sign-preserving** — negative spend
 * reversals MUST NOT be clamped to 0 (Pre-mortem F5).
 */
export function parseMetric(v: unknown): number {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const n = Number(String(v).trim());
  return Number.isFinite(n) ? n : 0;
}

/**
 * Divide a Pinterest micro-dollar integer by 1M. Sign-preserving.
 * `5_000_000` → `5.0`; `-5_000_000` → `-5.0`; `null` → `0`.
 */
export function parseMicroDollar(v: unknown): number {
  return parseMetric(v) / 1_000_000;
}

/**
 * Defensive percent normaliser.
 *   - Fraction form (`0.0234`) → unchanged.
 *   - Display-percent form (`2.34`) → `0.0234`.
 *   - `null`/non-finite → `0`.
 *
 * The asymmetric `> 1` guard is intentional. Mathematically a rate cannot
 * exceed 100% (i.e. > 1 in fraction form), so any value strictly greater
 * than 1 must be a display-percent. The single exception is metrics like
 * ROAS which are NOT percentages — those flow through `parseMetric` instead.
 */
export function parsePercent(v: unknown): number {
  const n = parseMetric(v);
  return n > 1 ? n / 100 : n;
}

/** Pinterest video watch time is reported in seconds; pass-through `parseMetric`. */
export function parseDuration(v: unknown): number {
  return parseMetric(v);
}

/** Coerce a string identifier preserving null-ish → empty string. */
function parseString(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v);
}

// ============================================================================
// Field maps — single source of truth for Pinterest column → camelCase key
// ============================================================================

interface FieldSpec {
  /** camelCase key emitted on the output record. */
  readonly out: string;
  /** Coercion function. Defaults to `parseMetric` (Integer/Decimal/Ratio). */
  readonly parse: (v: unknown) => number | string;
}

const M = (out: string, parse: FieldSpec["parse"] = parseMetric): FieldSpec => ({ out, parse });

/**
 * Master Pinterest API column → spec map. **All 185 metric columns** plus
 * the entity-identifier columns are listed here. Transformers iterate this
 * map to produce a record; columns absent from a raw row are simply not
 * emitted.
 *
 * Fields ending in `_IN_MICRO_DOLLAR` use `parseMicroDollar`. Fields named
 * `*_RATE`, `CTR`, `CTR_2`, `ECTR`, `EENGAGEMENT_RATE` use `parsePercent`.
 * `*_VIDEO_AVG_WATCHTIME_*` and `VIDEO_LENGTH` use `parseDuration` (alias
 * for `parseMetric`, kept distinct for documentation). String identifier
 * columns (CAMPAIGN_ID, AD_NAME, etc.) use `parseString`.
 */
const FIELD_MAP: Record<string, FieldSpec> = {
  // -------------------- Delivery & Reach (6) --------------------
  IMPRESSION_1: M("paidImpressions"),
  IMPRESSION_2: M("earnedImpressions"),
  TOTAL_IMPRESSION: M("totalImpressions"),
  TOTAL_IMPRESSION_FREQUENCY: M("impressionFrequency"),
  TOTAL_IMPRESSION_USER: M("reachUniqueUsers"),
  IMPRESSION_1_GROSS: M("grossImpressions"),

  // -------------------- Spend & Cost Efficiency (9) --------------------
  SPEND_IN_MICRO_DOLLAR: M("spendMicro", parseMicroDollar),
  SPEND_IN_DOLLAR: M("spend"),
  ECPM_IN_MICRO_DOLLAR: M("ecpmMicro", parseMicroDollar),
  ECPM_IN_DOLLAR: M("ecpm"),
  CPM_IN_DOLLAR: M("cpm"),
  CPM_IN_MICRO_DOLLAR: M("cpmMicro", parseMicroDollar),
  ECPC_IN_DOLLAR: M("paidCpc"),
  ECPC_IN_MICRO_DOLLAR: M("paidCpcMicro", parseMicroDollar),
  COST_PER_OUTBOUND_CLICK_IN_DOLLAR: M("costPerOutboundClick"),

  // -------------------- Engagement — Paid `_1` (6) --------------------
  CLICKTHROUGH_1: M("paidClicks"),
  CTR: M("paidCtr", parsePercent),
  OUTBOUND_CLICK_1: M("paidOutboundClicks"),
  OUTBOUND_CTR: M("paidOutboundCtr", parsePercent),
  ENGAGEMENT_1: M("paidEngagements"),
  ENGAGEMENT_RATE: M("paidEngagementRate", parsePercent),

  // -------------------- Engagement — Earned `_2` (6) --------------------
  CLICKTHROUGH_2: M("earnedClicks"),
  CTR_2: M("earnedCtr", parsePercent),
  ECTR: M("earnedCtrAlternate", parsePercent),
  OUTBOUND_CLICK_2: M("earnedOutboundClicks"),
  ENGAGEMENT_2: M("earnedEngagements"),
  EENGAGEMENT_RATE: M("earnedEngagementRate", parsePercent),

  // -------------------- Engagement — Total Rollups (5) --------------------
  TOTAL_CLICKTHROUGH: M("totalClickthroughs"),
  TOTAL_ENGAGEMENT: M("totalEngagements"),
  TOTAL_DESTINATION_VIEWS: M("totalDestinationViews"),
  TOTAL_REPIN_RATE: M("totalRepinRate", parsePercent),
  ECPE_IN_DOLLAR: M("costPerEngagement"),

  // -------------------- Saves (Repins) (5) --------------------
  REPIN_1: M("paidSaves"),
  REPIN_2: M("earnedSaves"),
  REPIN_RATE: M("paidSaveRate", parsePercent),
  SHOWCASE_SUBPAGE_CLOSEUP: M("showcaseCloseupClicks"),
  SHOWCASE_AVERAGE_SUBPAGE_CLOSEUP_PER_SESSION: M("showcaseAvgCloseupPerSession"),

  // -------------------- Video — Paid `_1` (10) --------------------
  VIDEO_MRC_VIEWS_1: M("paidVideoMrcViews"),
  VIDEO_3SEC_VIEWS_1: M("paidVideo3sViews"),
  VIDEO_P0_COMBINED_1: M("paidVideoP0"),
  VIDEO_P25_COMBINED_1: M("paidVideoP25"),
  VIDEO_P50_COMBINED_1: M("paidVideoP50"),
  VIDEO_P75_COMBINED_1: M("paidVideoP75"),
  VIDEO_P95_COMBINED_1: M("paidVideoP95"),
  VIDEO_P100_COMPLETE_1: M("paidVideoP100"),
  VIDEO_AVG_WATCHTIME_IN_SECOND_1: M("paidVideoAvgWatchTimeSec", parseDuration),
  VIDEO_LENGTH: M("videoLengthSec", parseDuration),

  // -------------------- Video — Earned `_2` (9) --------------------
  VIDEO_MRC_VIEWS_2: M("earnedVideoMrcViews"),
  VIDEO_3SEC_VIEWS_2: M("earnedVideo3sViews"),
  VIDEO_P0_COMBINED_2: M("earnedVideoP0"),
  VIDEO_P25_COMBINED_2: M("earnedVideoP25"),
  VIDEO_P50_COMBINED_2: M("earnedVideoP50"),
  VIDEO_P75_COMBINED_2: M("earnedVideoP75"),
  VIDEO_P95_COMBINED_2: M("earnedVideoP95"),
  VIDEO_P100_COMPLETE_2: M("earnedVideoP100"),
  VIDEO_AVG_WATCHTIME_IN_SECOND_2: M("earnedVideoAvgWatchTimeSec", parseDuration),

  // -------------------- Video — Total Rollups (9) --------------------
  TOTAL_VIDEO_3SEC_VIEWS: M("totalVideo3sViews"),
  TOTAL_VIDEO_MRC_VIEWS: M("totalVideoMrcViews"),
  TOTAL_VIDEO_P0_COMBINED: M("totalVideoP0"),
  TOTAL_VIDEO_P25_COMBINED: M("totalVideoP25"),
  TOTAL_VIDEO_P50_COMBINED: M("totalVideoP50"),
  TOTAL_VIDEO_P75_COMBINED: M("totalVideoP75"),
  TOTAL_VIDEO_P95_COMBINED: M("totalVideoP95"),
  TOTAL_VIDEO_P100_COMPLETE: M("totalVideoP100"),
  TOTAL_VIDEO_AVG_WATCHTIME_IN_SECOND: M("totalVideoAvgWatchTimeSec", parseDuration),

  // -------------------- Video — Cost Metrics (7) --------------------
  CPV_IN_MICRO_DOLLAR: M("cpvMicro", parseMicroDollar),
  ECPV_IN_DOLLAR: M("ecpv"),
  CPCV_IN_MICRO_DOLLAR: M("cpcvMicro", parseMicroDollar),
  ECPCV_IN_DOLLAR: M("ecpcv"),
  CPCV_P95_IN_MICRO_DOLLAR: M("cpcvP95Micro", parseMicroDollar),
  ECPCV_P95_IN_DOLLAR: M("ecpcvP95"),
  PAID_VIDEO_VIEWABLE_RATE: M("paidVideoViewableRate", parsePercent),

  // -------------------- Conversions — Click-Attributed (10) --------------------
  TOTAL_CONVERSIONS: M("totalClickConversions"),
  TOTAL_CLICK_PAGE_VISIT: M("clickPageVisit"),
  TOTAL_CLICK_SIGNUP: M("clickSignup"),
  TOTAL_CLICK_CHECKOUT: M("clickCheckout"),
  TOTAL_CLICK_ADD_TO_CART: M("clickAddToCart"),
  TOTAL_CLICK_LEAD: M("clickLead"),
  TOTAL_CLICK_SEARCH: M("clickSearch"),
  TOTAL_CLICK_CUSTOM: M("clickCustom"),
  TOTAL_CLICK_WATCH_VIDEO: M("clickWatchVideo"),
  TOTAL_CLICK_CHECKOUT_VALUE_IN_MICRO_DOLLAR: M("clickCheckoutValue", parseMicroDollar),

  // -------------------- Conversions — View-Attributed (7) --------------------
  TOTAL_VIEW_PAGE_VISIT: M("viewPageVisit"),
  TOTAL_VIEW_SIGNUP: M("viewSignup"),
  TOTAL_VIEW_CHECKOUT: M("viewCheckout"),
  TOTAL_VIEW_ADD_TO_CART: M("viewAddToCart"),
  TOTAL_VIEW_CUSTOM: M("viewCustom"),
  TOTAL_VIEW_VIEW_CATEGORY: M("viewCategory"),
  TOTAL_VIEW_CHECKOUT_VALUE_IN_MICRO_DOLLAR: M("viewCheckoutValue", parseMicroDollar),

  // -------------------- Conversions — Engagement-Attributed (3) --------------------
  TOTAL_ENGAGEMENT_PAGE_VISIT: M("engagementPageVisit"),
  TOTAL_ENGAGEMENT_CHECKOUT: M("engagementCheckout"),
  TOTAL_ENGAGEMENT_SIGNUP: M("engagementSignup"),

  // -------------------- Conversion Rollups — Total (12) --------------------
  TOTAL_PAGE_VISIT: M("totalPageVisits"),
  TOTAL_SIGNUP: M("totalSignups"),
  TOTAL_CHECKOUT: M("totalCheckouts"),
  TOTAL_CUSTOM: M("totalCustomEvents"),
  TOTAL_ADD_TO_CART: M("totalAddToCart"),
  TOTAL_LEAD: M("totalLeads"),
  TOTAL_SEARCH: M("totalSearches"),
  TOTAL_WATCH_VIDEO: M("totalWatchVideoEvents"),
  TOTAL_VIEW_CATEGORY: M("totalViewCategoryEvents"),
  TOTAL_APP_INSTALL: M("totalAppInstalls"),
  TOTAL_CONVERSIONS_QUANTITY: M("totalConversionsQuantity"),
  TOTAL_CONVERSIONS_VALUE_IN_MICRO_DOLLAR: M("totalConversionsValue", parseMicroDollar),

  // -------------------- Conversion Rates (10) --------------------
  TOTAL_CHECKOUT_CONVERSION_RATE: M("checkoutConversionRate", parsePercent),
  TOTAL_ADD_TO_CART_CONVERSION_RATE: M("addToCartConversionRate", parsePercent),
  TOTAL_SIGNUP_CONVERSION_RATE: M("signupConversionRate", parsePercent),
  TOTAL_PAGE_VISIT_CONVERSION_RATE: M("pageVisitConversionRate", parsePercent),
  TOTAL_LEAD_CONVERSION_RATE: M("leadConversionRate", parsePercent),
  TOTAL_SEARCH_CONVERSION_RATE: M("searchConversionRate", parsePercent),
  TOTAL_WATCH_VIDEO_CONVERSION_RATE: M("watchVideoConversionRate", parsePercent),
  TOTAL_CUSTOM_CONVERSION_RATE: M("customConversionRate", parsePercent),
  TOTAL_VIEW_CATEGORY_CONVERSION_RATE: M("viewCategoryConversionRate", parsePercent),
  TOTAL_UNKNOWN_CONVERSION_RATE: M("unknownConversionRate", parsePercent),

  // -------------------- Revenue Rollups — Total Value (8) --------------------
  TOTAL_CHECKOUT_VALUE_IN_MICRO_DOLLAR: M("totalCheckoutValue", parseMicroDollar),
  TOTAL_CUSTOM_VALUE_IN_MICRO_DOLLAR: M("totalCustomEventValue", parseMicroDollar),
  TOTAL_ADD_TO_CART_VALUE_IN_MICRO_DOLLAR: M("totalAddToCartValue", parseMicroDollar),
  TOTAL_LEAD_VALUE_IN_MICRO_DOLLAR: M("totalLeadValue", parseMicroDollar),
  TOTAL_SEARCH_VALUE_IN_MICRO_DOLLAR: M("totalSearchValue", parseMicroDollar),
  TOTAL_PAGE_VISIT_VALUE_IN_MICRO_DOLLAR: M("totalPageVisitValue", parseMicroDollar),
  TOTAL_WATCH_VIDEO_VALUE_IN_MICRO_DOLLAR: M("totalWatchVideoValue", parseMicroDollar),
  TOTAL_SIGNUP_VALUE_IN_MICRO_DOLLAR: M("totalSignupValue", parseMicroDollar),

  // -------------------- Cost Per Action & ROAS (5) --------------------
  PAGE_VISIT_COST_PER_ACTION: M("pageVisitCostPerAction"),
  PAGE_VISIT_ROAS: M("pageVisitRoas"),
  CHECKOUT_ROAS: M("checkoutRoas"),
  CUSTOM_ROAS: M("customEventRoas"),
  APP_INSTALL_COST_PER_ACTION: M("appInstallCostPerAction"),

  // -------------------- Channel — Web (10) --------------------
  TOTAL_WEB_CHECKOUT: M("totalWebCheckouts"),
  TOTAL_WEB_ADD_TO_CART: M("totalWebAddToCart"),
  TOTAL_WEB_PAGE_VISIT: M("totalWebPageVisits"),
  TOTAL_WEB_SIGNUP: M("totalWebSignups"),
  TOTAL_WEB_WATCH_VIDEO: M("totalWebWatchVideo"),
  TOTAL_WEB_LEAD: M("totalWebLeads"),
  TOTAL_WEB_SEARCH: M("totalWebSearches"),
  TOTAL_WEB_VIEW_CATEGORY: M("totalWebViewCategory"),
  TOTAL_WEB_CUSTOM: M("totalWebCustom"),
  WEB_CHECKOUT_ROAS: M("webCheckoutRoas"),

  // -------------------- Channel — In-App (6) --------------------
  TOTAL_INAPP_CHECKOUT: M("totalInappCheckouts"),
  TOTAL_INAPP_ADD_TO_CART: M("totalInappAddToCart"),
  TOTAL_INAPP_SIGNUP: M("totalInappSignups"),
  TOTAL_INAPP_SEARCH: M("totalInappSearches"),
  TOTAL_INAPP_APP_INSTALL: M("totalInappAppInstalls"),
  INAPP_CHECKOUT_ROAS: M("inappCheckoutRoas"),

  // -------------------- Channel — Offline (5) --------------------
  TOTAL_OFFLINE_CHECKOUT: M("totalOfflineCheckouts"),
  TOTAL_OFFLINE_SIGNUP: M("totalOfflineSignups"),
  TOTAL_OFFLINE_LEAD: M("totalOfflineLeads"),
  TOTAL_OFFLINE_CUSTOM: M("totalOfflineCustom"),
  OFFLINE_CHECKOUT_ROAS: M("offlineCheckoutRoas"),

  // -------------------- Channel — Pinterest-Native (3) --------------------
  TOTAL_PINTEREST_CHECKOUT: M("totalPinterestCheckouts"),
  TOTAL_PINTEREST_CHECKOUT_VALUE_IN_MICRO_DOLLAR: M("totalPinterestCheckoutValue", parseMicroDollar),
  PINTEREST_CHECKOUT_ROAS: M("pinterestCheckoutRoas"),

  // -------------------- Cross-Device Attribution (5 sample) --------------------
  TOTAL_PAGE_VISIT_DESKTOP_ACTION_TO_DESKTOP_CONVERSION: M("pageVisitDesktopToDesktop"),
  TOTAL_PAGE_VISIT_DESKTOP_ACTION_TO_MOBILE_CONVERSION: M("pageVisitDesktopToMobile"),
  TOTAL_PAGE_VISIT_MOBILE_ACTION_TO_DESKTOP_CONVERSION: M("pageVisitMobileToDesktop"),
  TOTAL_CHECKOUT_DESKTOP_ACTION_TO_DESKTOP_CONVERSION: M("checkoutDesktopToDesktop"),
  TOTAL_CHECKOUT_MOBILE_ACTION_TO_MOBILE_CONVERSION: M("checkoutMobileToMobile"),

  // -------------------- Web Sessions (3) --------------------
  TOTAL_WEB_SESSIONS: M("totalWebSessions"),
  WEB_SESSIONS_1: M("paidWebSessions"),
  WEB_SESSIONS_2: M("earnedWebSessions"),

  // -------------------- Leads (2) --------------------
  LEADS: M("leads"),
  COST_PER_LEAD: M("costPerLead"),

  // -------------------- Quantity Variants (5 sample) --------------------
  TOTAL_CHECKOUT_QUANTITY: M("totalCheckoutQuantity"),
  TOTAL_CLICK_PAGE_VISIT_QUANTITY: M("totalClickPageVisitQuantity"),
  TOTAL_VIEW_CHECKOUT_QUANTITY: M("totalViewCheckoutQuantity"),
  TOTAL_ENGAGEMENT_ADD_TO_CART_QUANTITY: M("totalEngagementAddToCartQuantity"),
  TOTAL_CLICK_CHECKOUT_QUANTITY: M("totalClickCheckoutQuantity"),

  // -------------------- Idea Pin (3) --------------------
  IDEA_PIN_PRODUCT_TAG_VISIT_1: M("paidIdeaPinProductTagVisits"),
  IDEA_PIN_PRODUCT_TAG_VISIT_2: M("earnedIdeaPinProductTagVisits"),
  TOTAL_IDEA_PIN_PRODUCT_TAG_VISIT: M("totalIdeaPinProductTagVisits"),

  // -------------------- Quiz (2) --------------------
  QUIZ_COMPLETED: M("quizCompleted"),
  QUIZ_COMPLETION_RATE: M("quizCompletionRate", parsePercent),

  // -------------------- Showcase (14) --------------------
  SHOWCASE_PIN_CLICKTHROUGH: M("showcasePinClickthroughs"),
  SHOWCASE_SUBPAGE_CLICKTHROUGH: M("showcaseSubpageClickthroughs"),
  SHOWCASE_SUBPIN_CLICKTHROUGH: M("showcaseSubpinClickthroughs"),
  SHOWCASE_SUBPAGE_IMPRESSION: M("showcaseSubpageImpressions"),
  SHOWCASE_SUBPIN_IMPRESSION: M("showcaseSubpinImpressions"),
  SHOWCASE_SUBPAGE_SWIPE_LEFT: M("showcaseSubpageSwipeLeft"),
  SHOWCASE_SUBPAGE_SWIPE_RIGHT: M("showcaseSubpageSwipeRight"),
  SHOWCASE_SUBPIN_SWIPE_LEFT: M("showcaseSubpinSwipeLeft"),
  SHOWCASE_SUBPIN_SWIPE_RIGHT: M("showcaseSubpinSwipeRight"),
  SHOWCASE_SUBPAGE_REPIN: M("showcaseSubpageRepins"),
  SHOWCASE_SUBPIN_REPIN: M("showcaseSubpinRepins"),
  SHOWCASE_CARD_THUMBNAIL_SWIPE_FORWARD: M("showcaseCardThumbnailSwipeForward"),
  SHOWCASE_CARD_THUMBNAIL_SWIPE_BACKWARD: M("showcaseCardThumbnailSwipeBackward"),
  ONSITE_CHECKOUTS_1: M("paidOnsiteCheckouts"),

  // -------------------- Entity identifiers (string-typed) --------------------
  CAMPAIGN_ID: M("campaignId", parseString),
  CAMPAIGN_NAME: M("campaignName", parseString),
  CAMPAIGN_STATUS: M("campaignStatus", parseString),
  CAMPAIGN_OBJECTIVE_TYPE: M("campaignObjectiveType", parseString),
  AD_GROUP_ID: M("adGroupId", parseString),
  AD_GROUP_NAME: M("adGroupName", parseString),
  AD_GROUP_STATUS: M("adGroupStatus", parseString),
  AD_GROUP_ENTITY_STATUS: M("adGroupEntityStatus", parseString),
  AD_ID: M("adId", parseString),
  AD_NAME: M("adName", parseString),
  PIN_ID: M("pinId", parseString),
  PIN_PROMOTION_TYPE: M("pinPromotionType", parseString),
  PIN_CREATIVE_TYPE: M("pinCreativeType", parseString),
  DATE: M("date", parseString),
  // -------------------- Targeting dim identifiers --------------------
  KEYWORD: M("keyword", parseString),
  AGE_BUCKET: M("ageBucket", parseString),
  GENDER: M("gender", parseString),
  AGE_BUCKET_AND_GENDER: M("ageBucketAndGender", parseString),
  COUNTRY: M("country", parseString),
  REGION: M("region", parseString),
  PLACEMENT: M("placement", parseString),
  PINNER_INTEREST: M("pinnerInterest", parseString),
  TARGETED_INTEREST: M("targetedInterest", parseString),
};

// ============================================================================
// Per-row transformer (single function, dispatched by report type)
// ============================================================================

/**
 * Coerce one raw Pinterest row into a typed record, then stamp `_reportType`.
 * Only columns present in `FIELD_MAP` are emitted — unknown columns from a
 * future Pinterest schema addition are silently dropped (forward-compatible).
 */
function transformRow(
  raw: Record<string, unknown>,
  reportType: PinterestAdsReportType,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [pinterestKey, value] of Object.entries(raw)) {
    const spec = FIELD_MAP[pinterestKey];
    if (!spec) continue;
    out[spec.out] = spec.parse(value);
  }
  out._reportType = reportType;
  return out;
}

// ============================================================================
// Public per-report-type transformer functions (named exports for testability)
// ============================================================================

export function transformOverviewRows(rows: ReadonlyArray<Record<string, unknown>>): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "overview"));
}
export function transformCampaignsRows(rows: ReadonlyArray<Record<string, unknown>>): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "campaigns"));
}
export function transformAdGroupsRows(rows: ReadonlyArray<Record<string, unknown>>): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "adGroups"));
}
export function transformAdsRows(rows: ReadonlyArray<Record<string, unknown>>): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "ads"));
}
export function transformKeywordsRows(rows: ReadonlyArray<Record<string, unknown>>): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "keywords"));
}
export function transformAgeGenderRows(rows: ReadonlyArray<Record<string, unknown>>): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "ageGender"));
}
export function transformGeoRows(rows: ReadonlyArray<Record<string, unknown>>): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "geo"));
}
export function transformInterestsRows(rows: ReadonlyArray<Record<string, unknown>>): Array<Record<string, unknown>> {
  return rows.map((r) => transformRow(r, "interests"));
}

/**
 * Dispatch table — mirrors `microsoft/transformers.TRANSFORMERS`. Used by
 * `pinterestAdsAdapter.fetchMetrics` to route each report type's raw rows
 * through the right transformer.
 */
export const TRANSFORMERS: Record<
  PinterestAdsReportType,
  (rows: ReadonlyArray<Record<string, unknown>>) => Array<Record<string, unknown>>
> = {
  overview: transformOverviewRows,
  campaigns: transformCampaignsRows,
  adGroups: transformAdGroupsRows,
  ads: transformAdsRows,
  keywords: transformKeywordsRows,
  ageGender: transformAgeGenderRows,
  geo: transformGeoRows,
  interests: transformInterestsRows,
};

// ============================================================================
// Test-only export (kept private to the module for production use)
// ============================================================================

/** Exposed for unit tests that audit the field-map coverage vs the metric catalogue. */
export const __FIELD_MAP_KEYS_FOR_TESTS = Object.keys(FIELD_MAP);
